import { z } from 'zod'
import { AI_MODELS, ALERT_TYPES, CHECK_INTERVALS, JOB_STATUSES, OAUTH_PROVIDERS, SENTIMENTS, TIERS } from '../types/enums.ts'

/**
 * Zod schemas for D1 row shapes.
 * All DB reads must be validated through one of these.
 */

export const TierSchema = z.enum(TIERS)
export const ModelSchema = z.enum(AI_MODELS)
export const JobStatusSchema = z.enum(JOB_STATUSES)
export const CheckIntervalSchema = z.enum(CHECK_INTERVALS)
export const AlertTypeSchema = z.enum(ALERT_TYPES)

export const UserRowSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  password_hash: z.string(),
  tier: TierSchema,
  credits_balance: z.number().default(0),
  trial_expires_at: z.number().nullable(),
  stripe_customer_id: z.string().nullable(),
  stripe_subscription_id: z.string().nullable(),
  subscription_status: z.enum(['active', 'canceled', 'past_due']).nullable(),
  billing_period_end: z.number().nullable(),
  created_at: z.number(),
})
export type UserRow = z.infer<typeof UserRowSchema>

export const BrandRowSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  name: z.string(),
  url: z.string().nullable(),
  industry: z.string(),
  prompt_template: z.string().nullable().optional().catch(null),
  created_at: z.number(),
})
export type BrandRow = z.infer<typeof BrandRowSchema>

export const PromptRowSchema = z.object({
  id: z.string(),
  brand_id: z.string(),
  prompt_text: z.string(),
  active: z.number().transform((n) => n === 1),
  created_at: z.number(),
}).transform((row) => ({
  ...row,
  promptText: row.prompt_text,
}))
export type PromptRow = z.infer<typeof PromptRowSchema>

export const ModelTargetRowSchema = z.object({
  id: z.string(),
  brand_id: z.string(),
  model: ModelSchema,
})
export type ModelTargetRow = z.infer<typeof ModelTargetRowSchema>

export const CheckJobRowSchema = z.object({
  id: z.string(),
  brand_id: z.string(),
  scheduled_at: z.number(),
  status: JobStatusSchema,
  interval: CheckIntervalSchema,
  retry_count: z.number(),
  error_msg: z.string().nullable(),
  created_at: z.number(),
})
export type CheckJobRow = z.infer<typeof CheckJobRowSchema>

export const SentimentSchema = z.enum(SENTIMENTS)

export const ResultRowSchema = z.object({
  id: z.string(),
  job_id: z.string(),
  prompt_id: z.string(),
  model: ModelSchema,
  mentioned: z.number().transform((n) => n === 1),
  rank_position: z.number().nullable(),
  sentiment: SentimentSchema.nullable().optional(),
  context_snippet: z.string().nullable(),
  competitors_snippet: z.string().nullable(),
  ai_answer: z.string().nullable(),
  raw_response: z.string().nullable(),
  checked_at: z.number(),
  latency_ms: z.number().nullable().optional().catch(null),
  input_tokens: z.number().nullable().optional().catch(null),
  output_tokens: z.number().nullable().optional().catch(null),
  model_cost_usd: z.number().nullable().optional().catch(null),
})
export type ResultRow = z.infer<typeof ResultRowSchema>

export const ResultCompetitorRowSchema = z.object({
  id: z.string(),
  result_id: z.string(),
  competitor_name: z.string(),
  raw_normalized_name: z.string(),
  position: z.number().nullable(),
  created_at: z.number(),
})
export type ResultCompetitorRow = z.infer<typeof ResultCompetitorRowSchema>

export const AlertRowSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  type: AlertTypeSchema,
  destination: z.string(),
  active: z.number().transform((n) => n === 1),
})
export type AlertRow = z.infer<typeof AlertRowSchema>

export const OAuthAccountRowSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  provider: z.enum(OAUTH_PROVIDERS),
  provider_id: z.string(),
  email: z.string().nullable(),
  created_at: z.number(),
})
export type OAuthAccountRow = z.infer<typeof OAuthAccountRowSchema>

export const WaitlistRowSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  business_name: z.string(),
  created_at: z.number(),
})
export type WaitlistRow = z.infer<typeof WaitlistRowSchema>

export const ContactSubmissionRowSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string().email(),
  subject: z.string(),
  message: z.string(),
  created_at: z.number(),
})
export type ContactSubmissionRow = z.infer<typeof ContactSubmissionRowSchema>
